import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import colors from "../../theme/colors";
import client from "../../api";
import { useDebouncedCallback } from "use-debounce";
import { cellIdToString } from "../../utils/labels";
import {
  cellsSlices,
  CellListState,
  MOVE_CONDITION_CONFIRMATIONS,
  MoveConditionConfirmations,
} from "./slice";
import { RootState } from "../../store";
import * as Sentry from "@sentry/react";

type Props = {
  open: boolean;
  onClose: () => void;
  stateKey: keyof typeof cellsSlices;
  selected: Cell[];
  onSuccess?: () => void;
};

type ExperimentOption = {
  id: number;
  alt_id: string;
};

type ConditionOption = {
  cell_condition_id: number;
  name: string;
  exp_id: number;
};

/**
 * This modal lets users move cell(s) between conditions or experiments. They can search for an experiment or a
 * condition via two dropdowns(one for each). If they start typing a condition, the experiment field clears if it is
 * populated, and vice versa. Warnings will appear for Additional cells affected(moving experiments) and template
 * mismatches(moving conditions). Errors will appear for cell assembly mismatches(moving between conditions).
 */
const MoveCellsToConditionOrExperimentModal = ({
  open,
  onClose,
  stateKey,
  selected,
  onSuccess,
}: Props) => {
  const dispatch = useDispatch();

  // Redux state
  const {
    status: {
      moveCellsToExperiment: moveExpStatus,
      moveCellsToCondition: moveCondStatus,
    },
    warning: {
      moveCellsToExperiment: moveExpWarning,
      moveCellsToCondition: moveCondWarning,
    },
    warningTitle: { moveCellsToCondition: moveCondWarningTitle },
    error: {
      moveCellsToExperiment: moveExpError,
      moveCellsToCondition: moveCondError,
    },
    errorTitle: {
      moveCellsToExperiment: moveExpErrorTitle,
      moveCellsToCondition: moveCondErrorTitle,
    },
    movedCellIds,
    moveWorkOrderWarning,
  } = useSelector<RootState, CellListState>(
    (state) => state[`cellList_${stateKey}` as keyof RootState] as CellListState
  );

  // Local state for search and selections
  const [targetType, setTargetType] = useState<
    "condition" | "experiment" | null
  >(null);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [experimentOptions, setExperimentOptions] = useState<
    ExperimentOption[]
  >([]);
  const [conditionOptions, setConditionOptions] = useState<ConditionOption[]>(
    []
  );
  const [selectedExperiment, setSelectedExperiment] =
    useState<ExperimentOption | null>(null);
  const [selectedCondition, setSelectedCondition] =
    useState<ConditionOption | null>(null);
  const [loadingExperiments, setLoadingExperiments] = useState(false);
  const [loadingConditions, setLoadingConditions] = useState(false);
  // Changes the user has confirmed for this move to a condition.
  const [conditionConfirmations, setConditionConfirmations] =
    useState<MoveConditionConfirmations>({});

  // Extract stable values
  const expId = selected[0]?.condition?.exp_id;
  const currentConditionId = selected[0]?.condition?.cell_condition_id;
  const cellIds = selected.map((cell) => cell.cell_id);

  // Reset local state on modal close
  useEffect(() => {
    if (!open) {
      setTargetType(null);
      setSearchText(null);
      setExperimentOptions([]);
      setConditionOptions([]);
      setSelectedExperiment(null);
      setSelectedCondition(null);
      setLoadingExperiments(false);
      setLoadingConditions(false);
      setConditionConfirmations({});
      // Note: Redux state is reset in handleClose to avoid race conditions
    }
  }, [open]);

  // Clear confirmations when the target condition changes.
  useEffect(() => {
    setConditionConfirmations({});
  }, [selectedCondition]);

  // Reset loading states when switching between modes
  useEffect(() => {
    if (targetType === "experiment") {
      setLoadingConditions(false);
    } else if (targetType === "condition") {
      setLoadingExperiments(false);
    }
  }, [targetType]);

  // Experiment search
  const _handleExperimentLookup = useCallback(async () => {
    if (searchText === null || targetType !== "experiment") return;

    // Check if we're still in experiment mode before starting
    const currentTargetType = targetType;

    setLoadingExperiments(true);
    try {
      const searchTerm = searchText.toUpperCase().replace(/^EXP/, "");
      const encodedSearch = encodeURIComponent(searchTerm);
      const endpoint = `meta/experiments/ids?search=${encodedSearch}&return-fields=owner__name`;
      const response = await client.get(endpoint);

      // Only update state if still in experiment mode (avoid race condition)
      if (currentTargetType === "experiment" && targetType === "experiment") {
        const formattedData = response.data
          .map((obj: [number, string]) => ({
            id: obj[0],
            alt_id: obj[1],
          }))
          .filter((exp: ExperimentOption) => exp.id !== expId); // Filter out current experiment
        setExperimentOptions(formattedData);
      }
    } catch (err) {
      if (targetType === "experiment") {
        setExperimentOptions([]);
      }
      Sentry.captureException(err);
    } finally {
      if (targetType === "experiment") {
        setLoadingExperiments(false);
      }
    }
  }, [searchText, expId, targetType]);

  const handleExperimentLookup = useDebouncedCallback(
    _handleExperimentLookup,
    400,
    {
      leading: true,
      trailing: true,
    }
  );

  useEffect(() => {
    if (targetType === "experiment") {
      handleExperimentLookup();
    }
  }, [searchText, handleExperimentLookup, targetType]);

  // Condition search
  const _handleConditionLookup = useCallback(async () => {
    if (searchText === null || !expId || targetType !== "condition") return;

    // Check if we're still in condition mode before starting
    const currentTargetType = targetType;

    setLoadingConditions(true);
    try {
      const searchParam = searchText
        ? `name__contains=${encodeURIComponent(searchText.toUpperCase())}&`
        : "";
      const endpoint = `meta/cell-conditions/advanced?${searchParam}exp_id=${expId}`;
      const response = await client.get(endpoint);

      // Only update state if still in condition mode (avoid race condition)
      if (currentTargetType === "condition" && targetType === "condition") {
        const filteredData = (response.data || []).filter(
          (cond: ConditionOption) =>
            cond.cell_condition_id !== currentConditionId
        ); // Filter out current condition
        setConditionOptions(filteredData);
      }
    } catch (err) {
      if (targetType === "condition") {
        setConditionOptions([]);
      }
      Sentry.captureException(err);
    } finally {
      if (targetType === "condition") {
        setLoadingConditions(false);
      }
    }
  }, [searchText, expId, currentConditionId, targetType]);

  const handleConditionLookup = useDebouncedCallback(
    _handleConditionLookup,
    400,
    {
      leading: true,
      trailing: true,
    }
  );

  useEffect(() => {
    if (targetType === "condition") {
      handleConditionLookup();
    }
  }, [searchText, handleConditionLookup, targetType]);

  // Computed values
  const isSuccess =
    moveExpStatus === "succeeded" || moveCondStatus === "succeeded";
  const currentWarning = moveExpWarning || moveCondWarning;
  const warningText = moveCondWarning
    ? { title: moveCondWarningTitle, prompt: "Do you want to proceed?" }
    : {
        title: "Additional Cells Will Be Affected",
        prompt:
          "Moving cells to this experiment will affect more cells than originally selected. Do you want to proceed?",
      };
  const currentError = moveExpError || moveCondError;
  const currentErrorTitle = moveExpErrorTitle || moveCondErrorTitle;
  const canSubmit =
    (selectedExperiment !== null || selectedCondition !== null) &&
    moveExpStatus !== "loading" &&
    moveCondStatus !== "loading";

  // Handle submit
  const handleSubmit = useCallback(
    (confirmed = false) => {
      if (selectedExperiment) {
        dispatch(
          cellsSlices[stateKey].moveCellsToExperiment({
            cellIds,
            targetExpId: selectedExperiment.id,
            allowAdditionalCells: confirmed,
          })
        );
      } else if (selectedCondition) {
        // Confirming adds the pending confirmation's flag to the earlier ones.
        const confirmations =
          confirmed && moveCondWarningTitle
            ? {
                ...conditionConfirmations,
                [MOVE_CONDITION_CONFIRMATIONS[
                  moveCondWarningTitle as keyof typeof MOVE_CONDITION_CONFIRMATIONS
                ]]: true,
              }
            : conditionConfirmations;
        setConditionConfirmations(confirmations);
        dispatch(
          cellsSlices[stateKey].moveCellsToCondition({
            cellIds,
            targetConditionName: selectedCondition.name,
            ...confirmations,
          })
        );
      }
    },
    [
      selectedExperiment,
      selectedCondition,
      cellIds,
      dispatch,
      stateKey,
      moveCondWarningTitle,
      conditionConfirmations,
    ]
  );

  const handleClose = useCallback(() => {
    const wasSuccess = isSuccess;

    // Always reset Redux state when closing to prevent stale success state
    dispatch(cellsSlices[stateKey].resetMoveCellsToExperiment());
    dispatch(cellsSlices[stateKey].resetMoveCellsToCondition());

    if (wasSuccess) {
      onSuccess?.();
    }
    onClose();
  }, [onClose, isSuccess, onSuccess, dispatch, stateKey]);

  // Success view
  if (isSuccess) {
    const movedCount = movedCellIds.length || cellIds.length;
    const cellLabel = movedCount === 1 ? "cell" : "cells";

    return (
      <Modal open={open} onClose={handleClose} maxWidth="md">
        <Typography variant="h2">Move Successful</Typography>
        <Box mt={4}>
          <Typography variant="body1" gutterBottom>
            Successfully moved {movedCount} {cellLabel} to:
          </Typography>

          <Box
            mt={3}
            p={2}
            style={{ backgroundColor: colors.striping, borderRadius: 4 }}
          >
            {selectedExperiment && (
              <>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Experiment: EXP{selectedExperiment.id}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Owner: {selectedExperiment.alt_id}
                </Typography>
              </>
            )}
            {selectedCondition && (
              <>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Condition: {selectedCondition.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Experiment: EXP{selectedCondition.exp_id}
                </Typography>
              </>
            )}
          </Box>

          <Box mt={3}>
            <Typography variant="body2">
              Cells:{" "}
              {(movedCellIds.length ? movedCellIds : cellIds)
                .map(cellIdToString)
                .join(", ")}
            </Typography>
          </Box>

          {moveWorkOrderWarning && (
            <Box
              mt={3}
              p={2}
              style={{
                backgroundColor: `${colors.accent.yellow}20`,
                border: `1px solid ${colors.accent.yellow}`,
                borderRadius: 4,
              }}
            >
              <Typography variant="body2">⚠️ {moveWorkOrderWarning}</Typography>
            </Box>
          )}

          <Box mt={6} display="flex" justifyContent="flex-end">
            <Button color="cta" size="small" onClick={handleClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }

  // Main form
  return (
    <Modal open={open} onClose={handleClose} maxWidth="md">
      <Typography variant="h2">
        Move {selected.length} cell{selected.length !== 1 ? "s" : ""}
      </Typography>

      <Box mt={4}>
        {/* Only show instruction text if there's no warning or error */}
        {!currentWarning && !currentError && (
          <Typography variant="body1" gutterBottom>
            Select either a cell condition or an experiment to move the selected
            cell{selected.length !== 1 ? "s" : ""} to:
          </Typography>
        )}

        {/* Error message */}
        {currentError && (
          <Box
            mt={2}
            mb={2}
            p={2}
            style={{
              backgroundColor: `${colors.accent.red}10`,
              border: `1px solid ${colors.accent.red}`,
              borderRadius: 4,
            }}
          >
            <Typography
              variant="body2"
              style={{ fontWeight: 600, marginBottom: 8 }}
              color="error"
            >
              {currentErrorTitle || "Error moving cells"}
            </Typography>
            <Typography variant="body2" color="error">
              {currentError}
            </Typography>
          </Box>
        )}

        {/* Warning confirmation */}
        {currentWarning && (
          <Box
            mt={2}
            mb={2}
            p={2}
            style={{
              backgroundColor: `${colors.accent.yellow}20`,
              border: `1px solid ${colors.accent.yellow}`,
              borderRadius: 4,
            }}
          >
            <Typography
              variant="body2"
              style={{ fontWeight: 600, marginBottom: 8 }}
            >
              ⚠️ {warningText.title}
            </Typography>
            <Typography variant="body2" style={{ marginBottom: 16 }}>
              {currentWarning}
            </Typography>
            <Typography
              variant="body2"
              style={{ marginBottom: 16, fontStyle: "italic" }}
            >
              {warningText.prompt}
            </Typography>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                color="secondary"
                size="small"
                onClick={() => {
                  dispatch(cellsSlices[stateKey].resetMoveCellsToExperiment());
                  dispatch(cellsSlices[stateKey].resetMoveCellsToCondition());
                  setConditionConfirmations({});
                }}
              >
                Cancel Operation
              </Button>
              <Button
                color="cta"
                size="small"
                onClick={() => handleSubmit(true)}
                disabled={
                  moveExpStatus === "loading" || moveCondStatus === "loading"
                }
                startIcon={
                  moveExpStatus === "loading" ||
                  moveCondStatus === "loading" ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null
                }
              >
                {moveExpStatus === "loading" || moveCondStatus === "loading"
                  ? "Moving..."
                  : "Proceed Anyway"}
              </Button>
            </Box>
          </Box>
        )}

        {/* Condition search - only show if no warning */}
        {!currentWarning && (
          <Box mt={4}>
            <Typography className="small" color="textSecondary" gutterBottom>
              Cell Condition
            </Typography>
            <Autocomplete
              options={conditionOptions}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) =>
                option.cell_condition_id === value.cell_condition_id
              }
              value={selectedCondition}
              loading={loadingConditions}
              onOpen={() => {
                setTargetType("condition");
                setSearchText("");
              }}
              onClose={() => setSearchText(null)}
              onInputChange={(e, newValue) => {
                if (e && e.type === "change") {
                  setSearchText(newValue.toUpperCase());
                }
              }}
              onChange={(e, data) => {
                setSelectedCondition(data);
                if (data) {
                  setSelectedExperiment(null);
                  setTargetType("condition");
                  // Clear experiment error when switching to condition
                  dispatch(cellsSlices[stateKey].resetMoveCellsToExperiment());
                } else if (!selectedExperiment) {
                  setTargetType(null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  color="secondary"
                  placeholder="Search for a condition..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingConditions ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, opt) => (
                <li
                  {...props}
                  key={opt.cell_condition_id}
                  style={{ padding: 0 }}
                >
                  <Box py={1} px={2} width="100%">
                    <Typography variant="body2">{opt.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      EXP{opt.exp_id}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Box>
        )}

        {/* Experiment search - only show if no warning */}
        {!currentWarning && (
          <Box mt={4}>
            <Typography className="small" color="textSecondary" gutterBottom>
              Experiment
            </Typography>
            <Autocomplete
              options={experimentOptions}
              getOptionLabel={(option) => `EXP${option.id}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedExperiment}
              loading={loadingExperiments}
              onOpen={() => {
                setTargetType("experiment");
                setSearchText("");
              }}
              onClose={() => setSearchText(null)}
              onInputChange={(e, newValue) => {
                if (e && e.type === "change") {
                  setSearchText(newValue.toUpperCase());
                }
              }}
              onChange={(e, data) => {
                setSelectedExperiment(data);
                if (data) {
                  setSelectedCondition(null);
                  setTargetType("experiment");
                  // Clear condition error when switching to experiment
                  dispatch(cellsSlices[stateKey].resetMoveCellsToCondition());
                } else if (!selectedCondition) {
                  setTargetType(null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  color="secondary"
                  placeholder="Search for an experiment..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingExperiments ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, opt) => (
                <li {...props} key={opt.id} style={{ padding: 0 }}>
                  <Box py={1} px={2} width="100%">
                    <Typography variant="body2">EXP{opt.id}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Owner: {opt.alt_id}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Box>
        )}

        {/* Action buttons - only show if no warning */}
        {!currentWarning && (
          <Box mt={6} display="flex">
            <Box ml="auto" mr={2}>
              <Button
                color="secondary"
                size="small"
                onClick={handleClose}
                disabled={
                  moveExpStatus === "loading" || moveCondStatus === "loading"
                }
              >
                Cancel
              </Button>
            </Box>
            <Button
              color="cta"
              size="small"
              onClick={() => handleSubmit()}
              disabled={!canSubmit}
              startIcon={
                moveExpStatus === "loading" || moveCondStatus === "loading" ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null
              }
            >
              {moveExpStatus === "loading" || moveCondStatus === "loading"
                ? "Moving..."
                : "Move Cells"}
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default MoveCellsToConditionOrExperimentModal;
